import React, { useState, useEffect } from 'react';
import { Table, Tag, Space, Modal, Input, Form, message, Popconfirm, Row, Col, Tooltip } from 'antd';
import { useDispatch, useSelector } from 'react-redux';
import { adminGetContactsAsync, adminReplyToContactAsync, adminDeleteContactAsync } from '../adminSlice';
import { useTableParams } from '../../../hooks/useTableParams';
import useUserData from '../../../hooks/useUserData';

const Help = () => {
  const { userData } = useUserData();
  const isGuestAdmin = userData?.isGuestData;

  const dispatch = useDispatch();
  const { contactsData, isLoading } = useSelector((state) => state.admin);
  const contacts = contactsData?.contacts || [];

  const contactsTotal = useSelector((state) => state.admin.contactsData?.total) || 0;

  const [isReplyModalVisible, setIsReplyModalVisible] = useState(false);
  const [selectedContact, setSelectedContact] = useState(null);
  const [replyForm] = Form.useForm();

  const { tableParams, localSearch, handleTableChange, handleSearchChange, fetchData } = useTableParams(adminGetContactsAsync);

  const showReplyModal = (contact) => {
    setSelectedContact(contact);
    setIsReplyModalVisible(true);
  };

  const handleReplyCancel = () => {
    setIsReplyModalVisible(false);
    setSelectedContact(null);
    replyForm.resetFields();
  };

  const handleReplySubmit = async (values) => {
    if (!selectedContact) return;

    try {
      const response = await dispatch(adminReplyToContactAsync({
        id: selectedContact._id,
        replyMessage: values.replyMessage
      })).unwrap();

      if (response.status) {
        handleReplyCancel();
        fetchData();
      }
    } catch (error) {
      console.error(error);
    }
  };

  const handleDelete = async (id) => {
    try {
      await dispatch(adminDeleteContactAsync(id));
      fetchData();
    } catch (error) {
      console.error(error);
    }
  };

  const columns = [
    {
      title: 'Name',
      dataIndex: 'name',
      key: 'name',
      sorter: true,
      width: 150
    },
    {
      title: 'Email',
      dataIndex: 'email',
      key: 'email',
      sorter: true,
      width: 250
    },
    {
      title: 'Category',
      dataIndex: 'category',
      key: 'category',
      sorter: true,
      filters: [
        { text: 'General Inquiry', value: 'General Inquiry' },
        { text: 'Order Support', value: 'Order Support' },
        { text: 'Technical Issue', value: 'Technical Issue' },
        { text: 'Partnership', value: 'Partnership' },
        { text: 'Other', value: 'Other' },
      ],
      width: 150,
      render: (cat) => <Tag color="blue">{cat}</Tag>
    },
    {
      title: 'Message',
      dataIndex: 'message',
      key: 'message',
      ellipsis: true,
      sorter: true,
      width: 250
    },
    {
      title: "Date",
      dataIndex: 'createdAt',
      key: 'createdAt',
      sorter: true,
      width: 150,
      render: (date) => new Date(date).toLocaleString()
    },
    {
      title: "Status",
      dataIndex: 'isReplied',
      key: 'isReplied',
      sorter: true,
      width: 150,
      filters: [
        { text: 'REPLIED', value: 'true' },
        { text: 'PENDING', value: 'false' },
      ],
      render: (isReplied) => (
        <Tag color={isReplied ? 'green' : 'orange'}>
          {isReplied ? 'REPLIED' : 'PENDING'}
        </Tag>
      )
    },
    {
      title: 'Action',
      key: 'action',
      width: 150,
      render: (_, record) => {
        const disabledForGuest = isGuestAdmin && !record.isGuestData;

        const disableAction = (content) => {
          if (disabledForGuest) {
            return (
              <Tooltip title="Action restricted for guest accounts">
                <span style={{ display: 'inline-block', cursor: 'not-allowed' }}>
                  {React.cloneElement(content, { style: { ...content.props?.style, pointerEvents: 'none' } })}
                </span>
              </Tooltip>
            );
          }
          return content;
        };

        return (
          <Space size="middle">
            {disableAction(
              <button
                className="btn btn-sm btn-primary"
                onClick={() => showReplyModal(record)}
                disabled={record.isReplied || disabledForGuest}
              >
                {record.isReplied ? 'Replied' : 'Reply'}
              </button>
            )}

            {disableAction(
              <Popconfirm
                title="Delete this message?"
                description="Are you sure to delete this message? This action cannot be undone."
                onConfirm={() => handleDelete(record._id)}
                okText="Yes, Delete"
                cancelText="No"
                okButtonProps={{ danger: true }}
                disabled={disabledForGuest}
              >
                <button className="btn btn-sm btn-danger" disabled={disabledForGuest}>
                  <i className="fas fa-trash"></i>
                </button>
              </Popconfirm>
            )}
          </Space>
        )
      },
    },
  ];

  return (
    <div className="row g-2 my-2 bg-white border rounded p-3">
      <Row justify="space-between" align="middle" gutter={[16, 16]} className="mb-4">
        <Col xs={24} md={8}>
          <h5 className='h5 fw-bolder mb-0'>Help & Support (Contact Us Queries)</h5>
        </Col>
        <Col xs={24} md={16} style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px', flexWrap: 'wrap' }}>
          <Input
            placeholder="Search messages..."
            allowClear
            prefix={<span role="img" aria-label="search">🔍</span>}
            value={localSearch}
            onChange={handleSearchChange}
            style={{ width: '100%', maxWidth: '300px' }}
          />
          <button className="btn btn-outline-secondary btn-sm" onClick={() => handleSearchChange({ target: { value: localSearch } })}>
            <i className="fas fa-sync-alt"></i> Refresh
          </button>
        </Col>
      </Row>

      <Table
        dataSource={contacts}
        columns={columns}
        rowKey="_id"
        loading={isLoading}
        pagination={{
          ...tableParams.pagination,
          total: contactsTotal,
          showSizeChanger: true,
          position: ['bottomCenter']
        }}
        onChange={handleTableChange}
           scroll={{ x: 'max-content' }}
      />

      <Modal
        title={`Reply to ${selectedContact?.name}`}
        open={isReplyModalVisible}
        onCancel={handleReplyCancel}
        footer={null}
      >
        <div className="mb-3 p-3 bg-light rounded border">
          <strong>Original Message:</strong>
          <p className="mb-0 mt-2 text-muted">{selectedContact?.message}</p>
        </div>

        <Form form={replyForm} layout="vertical" onFinish={handleReplySubmit}>
          <Form.Item
            name="replyMessage"
            label="Your Reply"
            rules={[{ required: true, message: 'Please enter a reply message' }]}
          >
            <Input.TextArea rows={6} placeholder="Type your response here. It will be emailed directly to the customer." />
          </Form.Item>

          <Form.Item className="mb-0 text-end">
            <button type="button" className="btn btn-secondary me-2" onClick={handleReplyCancel}>Cancel</button>
            <button type="submit" className="btn btn-success" disabled={isLoading}>
              {isLoading ? 'Sending...' : 'Send Reply Email'}
            </button>
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
}

export default Help;